/**
 * 协议签署弹窗组件
 * 用于展示待签署协议并收集用户同意
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, LogOut, Loader2 } from "lucide-react";
import { useConsentStore } from "@/stores/consentStore";
import { useAgreementSign } from "@/hooks/useAgreementSign";
import { useUserStore } from "@/stores/userStore";
import AgreementViewer from "./AgreementViewer";
import type { AgreementType } from "@/types/agreement";

/**
 * 获取协议类型标题
 */
function getAgreementTitle(
  type: AgreementType | string,
  t: (key: string) => string
): string {
  const titles: Record<string, string> = {
    tos: t("agreement.tos"),
    privacy: t("agreement.privacy"),
    marketing: t("agreement.marketing"),
  };
  return titles[type] || type;
}

/**
 * 协议签署弹窗
 *
 * 功能：
 * - 展示待签署协议
 * - 多协议 Tab 切换
 * - 勾选同意和确认按钮
 * - 强制更新时的退出登录选项
 */
export default function ConsentModal() {
  const { t } = useTranslation("consent");
  const { isModalOpen, pendingAgreements, closeModal } = useConsentStore();
  const { signAgreements, isSigning, error } = useAgreementSign();
  const logout = useUserStore((state) => state.logout);
  const [agreed, setAgreed] = useState(false);

  const hasForceUpdate = pendingAgreements.some((a) => a.force_update);
  const agreementIds = pendingAgreements.map((a) => a.id);

  const handleConfirm = async () => {
    const success = await signAgreements(agreementIds);
    if (success) {
      setAgreed(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    closeModal();
  };

  // 强制更新时不允许关闭弹窗
  const canClose = !hasForceUpdate;

  // 没有待签署协议时不显示
  if (pendingAgreements.length === 0) {
    return null;
  }

  return (
    <Dialog open={isModalOpen} onOpenChange={canClose ? closeModal : undefined}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {hasForceUpdate && (
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
            )}
            {t("modal.title", "协议更新")}
          </DialogTitle>
          <DialogDescription>
            {hasForceUpdate
              ? t("modal.forceUpdateDesc", "协议已更新，请阅读并同意后继续使用")
              : t("modal.desc", "请阅读并同意以下协议")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden min-h-[300px]">
          {pendingAgreements.length === 1 ? (
            <AgreementViewer agreement={pendingAgreements[0]} />
          ) : (
            <Tabs
              defaultValue={pendingAgreements[0]?.type}
              className="h-full flex flex-col"
            >
              <TabsList className="w-full justify-start">
                {pendingAgreements.map((agreement) => (
                  <TabsTrigger
                    key={agreement.id}
                    value={agreement.type}
                    className="flex items-center gap-1"
                  >
                    {getAgreementTitle(agreement.type, t)}
                    {agreement.force_update && (
                      <Badge
                        variant="destructive"
                        className="ml-1 text-xs px-1 py-0"
                      >
                        {t("badge.forceUpdate", "必须")}
                      </Badge>
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>
              {pendingAgreements.map((agreement) => (
                <TabsContent
                  key={agreement.id}
                  value={agreement.type}
                  className="flex-1 mt-2"
                >
                  <AgreementViewer agreement={agreement} />
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>

        <DialogFooter className="flex-col gap-4 sm:flex-col">
          {error && (
            <p className="text-sm text-destructive text-center">{error}</p>
          )}

          <div className="flex items-center gap-2">
            <Checkbox
              id="agree"
              checked={agreed}
              onCheckedChange={(checked) => setAgreed(checked === true)}
            />
            <label htmlFor="agree" className="text-sm cursor-pointer">
              {t("modal.agreeText", "我已阅读并同意以上协议")}
            </label>
          </div>

          <div className="flex gap-2 w-full">
            {hasForceUpdate && (
              <Button
                variant="outline"
                onClick={handleLogout}
                className="flex-1"
              >
                <LogOut className="h-4 w-4 mr-2" />
                {t("modal.logout", "退出登录")}
              </Button>
            )}
            <Button
              onClick={handleConfirm}
              disabled={!agreed || isSigning}
              className="flex-1"
            >
              {isSigning ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t("modal.signing", "签署中...")}
                </>
              ) : (
                t("modal.confirm", "同意并继续")
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
